// AI System Types
// Tiered model routing, knowledge base, voice commands

// ============================================================================
// AI MODEL ROUTING
// ============================================================================

export type AIProvider = 'OPENAI' | 'ANTHROPIC' | 'GEMINI';

// OpenAI provider is enabled now that proxy-backed implementations are available.
export const OPENAI_PROVIDER_ENABLED = true;

export type AITaskType =
  | 'DAILY_BRIEFING'
  | 'EMAIL_DRAFT'
  | 'EMAIL_CLASSIFICATION'
  | 'QUOTE_PROPOSAL'
  | 'LOG_PARSING'
  | 'FOLLOW_UP'
  | 'RECEIPT_OCR'
  | 'DOCUMENT_ANALYSIS'
  | 'WIN_LOSS_ANALYSIS'
  | 'PRICING_RECOMMENDATION'
  | 'SOCIAL_CONTENT'
  | 'SELF_IMPROVEMENT_REVIEW'
  | 'VOICE_COMMAND'
  | 'SEMANTIC_SEARCH'
  | 'GENERAL_QUERY'
  | 'SCOPE_EXTRACTION'
  | 'SCOPE_EXTRACTION_DOC'
  | 'LABOR_GENERATION'
  | 'MATERIAL_SUGGESTION'
  | 'SCOPE_WRITING';

export type AITier = 'PREMIUM' | 'STANDARD' | 'MULTIMODAL' | 'EMBEDDINGS';

export interface ModelConfig {
  provider: AIProvider;
  model: string;
  maxTokens?: number;
  temperature?: number;
  tier: AITier;
}

export interface TaskContext {
  type: AITaskType;
  quoteValue?: number;
  hasImages?: boolean;
  complexity?: 'LOW' | 'MEDIUM' | 'HIGH';
  isStrategic?: boolean;
  customerId?: string;
  quoteId?: string;
}

export interface AIRoutingResult {
  config: ModelConfig;
  reason: string;
  estimatedCost: number; // cents
}

// Model configurations
export const MODEL_CONFIGS: Record<AITier, ModelConfig> = {
  PREMIUM: {
    provider: 'ANTHROPIC',
    model: 'claude-sonnet-4-20250514',
    maxTokens: 4096,
    temperature: 0.7,
    tier: 'PREMIUM',
  },
  STANDARD: {
    provider: 'GEMINI',
    model: 'gemini-2.0-flash',
    maxTokens: 2048,
    temperature: 0.7,
    tier: 'STANDARD',
  },
  MULTIMODAL: {
    provider: 'GEMINI',
    model: 'gemini-2.0-flash',
    maxTokens: 2048,
    temperature: 0.7,
    tier: 'MULTIMODAL',
  },
  EMBEDDINGS: {
    provider: 'OPENAI',
    model: 'text-embedding-3-small',
    tier: 'EMBEDDINGS',
  },
};

// Cost per 1M tokens (in cents)
export const MODEL_COSTS: Record<string, { input: number; output: number }> = {
  'claude-3-5-sonnet-20241022': { input: 300, output: 1500 },
  'claude-sonnet-4-20250514': { input: 300, output: 1500 },
  'gpt-4o-mini': { input: 15, output: 60 },
  'gpt-4o': { input: 250, output: 1000 },
  'gemini-2.0-flash': { input: 7.5, output: 30 },
  'text-embedding-3-small': { input: 2, output: 0 },
};

// ============================================================================
// VOICE COMMANDS
// ============================================================================

export type VoiceIntentType =
  | 'CREATE_TIME_ENTRY'
  | 'CREATE_QUOTE'
  | 'UPDATE_QUOTE'
  | 'CREATE_CHANGE_ORDER'
  | 'GET_STATUS'
  | 'SEARCH'
  | 'CREATE_TASK'
  | 'SEND_EMAIL'
  | 'LOG_EXPENSE'
  | 'MARK_COMPLETE'
  | 'SCHEDULE_JOB'
  | 'CREATE_REMINDER'
  | 'GENERAL_QUERY';

export interface VoiceIntent {
  type: VoiceIntentType;
  confidence: number;
  entities: Record<string, any>;
  originalText: string;
  suggestedAction?: string;
}

export interface VoiceCommandResult {
  success: boolean;
  intent: VoiceIntent;
  response: string;
  actionTaken?: string;
  data?: any;
}

// Entity types for voice commands
export interface TimeEntryEntities {
  hours: number;
  projectName?: string;
  quoteId?: string;
  description?: string;
  date?: string;
}

export interface QuoteEntities {
  customerName?: string;
  projectName?: string;
  description?: string;
  estimatedValue?: number;
}

export interface TaskEntities {
  title: string;
  dueDate?: string;
  priority?: 'LOW' | 'MEDIUM' | 'HIGH';
  relatedTo?: string;
}

// ============================================================================
// KNOWLEDGE BASE & EMBEDDINGS
// ============================================================================

export type DocumentType =
  | 'QUOTE_PROPOSAL'
  | 'EMAIL'
  | 'CUSTOMER_SUMMARY'
  | 'LESSON_LEARNED'
  | 'JOB_NOTES'
  | 'WIN_REASON'
  | 'LOSS_REASON';

export interface EmbeddedDocument {
  id: string;
  type: DocumentType;
  content: string;
  embedding?: number[];
  metadata: DocumentMetadata;
  createdAt: string;
}

export interface DocumentMetadata {
  quoteId?: string;
  customerId?: string;
  outcome?: 'WON' | 'LOST' | 'PENDING';
  date: string;
  tags: string[];
  source?: string;
}

export interface SemanticSearchResult {
  document: EmbeddedDocument;
  score: number; // Similarity score 0-1
  highlights?: string[];
}

export interface SemanticSearchOptions {
  query: string;
  limit?: number;
  minScore?: number;
  filters?: {
    type?: DocumentType[];
    customerId?: string;
    dateRange?: { start: string; end: string };
    tags?: string[];
  };
}

// ============================================================================
// LEARNED PATTERNS
// ============================================================================

export interface LearnedPattern {
  id: string;
  type: PatternType;
  description: string;
  confidence: number;
  sampleSize: number;
  data: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  validatedAt?: string;
}

export type PatternType =
  | 'PRICING'
  | 'CUSTOMER_PREFERENCE'
  | 'WIN_FACTOR'
  | 'LOSS_FACTOR'
  | 'SEASONAL'
  | 'JOB_DURATION'
  | 'MATERIAL_USAGE';

export interface PricingPattern extends LearnedPattern {
  type: 'PRICING';
  data: {
    jobType: string;
    customerCategory: string;
    avgWinningMarkup: number;
    avgLosingMarkup: number;
    optimalRange: { min: number; max: number };
  };
}

export interface CustomerPreferencePattern extends LearnedPattern {
  type: 'CUSTOMER_PREFERENCE';
  data: {
    customerId: string;
    preferredContactMethod: 'EMAIL' | 'PHONE' | 'TEXT';
    preferredContactTimes: string[];
    priceSensitivity: 'LOW' | 'MEDIUM' | 'HIGH';
    decisionSpeed: 'FAST' | 'MEDIUM' | 'SLOW';
  };
}

// ============================================================================
// AI USAGE TRACKING
// ============================================================================

export interface AIUsageLog {
  id: string;
  timestamp: string;
  taskType: AITaskType;
  tier: AITier;
  provider: AIProvider;
  model: string;
  inputTokens: number;
  outputTokens: number;
  latencyMs: number;
  cost: number; // cents
  success: boolean;
  error?: string;
}

export interface AIUsageStats {
  period: string;
  totalRequests: number;
  totalCost: number;
  byTier: Record<AITier, { requests: number; cost: number }>;
  byTaskType: Record<AITaskType, { requests: number; cost: number }>;
  avgLatency: number;
  errorRate: number;
}

// ============================================================================
// MODEL OVERRIDES
// ============================================================================

export interface ModelOverrides {
  tiers?: Partial<Record<AITier, { provider: AIProvider; model: string }>>;
  providers?: Partial<Record<AIProvider, string>>;
  tasks?: Partial<Record<AITaskType, { provider: AIProvider; model: string }>>;
}
